import { EventEmitter, Injectable, Output } from '@angular/core';
import { LytxWindow } from '@lytx/platform.ui.components';
import { Observable, of, Subject } from 'rxjs';
import { environment } from './../../environments/environment';
import { FleetTrackerConfig } from './fleet-tracker.config';

@Injectable()
export class MapScriptLoaderService {
  @Output() mapReady: EventEmitter<google.maps.Map> = new EventEmitter();
  googleMap: google.maps.Map;
  private _mapReady: Observable<void> = undefined;

  private readonly _document = document;

  //
  // Use AGM's script ID and callback function names.
  // This allows us to detect the presence of an AGM
  // component and share the underlying map API.
  //
  private _scriptId = 'agmGoogleMapsApiScript'; // AGM script ID
  private _callback = 'agmLazyMapsAPILoader'; // AGM callback name (until they change it!)

  constructor(
    private _config: FleetTrackerConfig,
    private window: LytxWindow,
  ) {}

  load(): Observable<void> {
    if (this.window.google && this.window.google.maps) {
      return of(undefined); // emit an explicit value: undefined
    }

    if (this._mapReady) {
      return this._mapReady;
    }

    //accommodate hot module loading: script already present but have new component instance
    const currentScript = this._document.getElementById(this._scriptId);
    if (currentScript) {
      const callback = this.window[this._callback]; // get existing callback if any
      this._mapReady = this.createScriptObservable(currentScript, callback); // replace existing callback
      return this._mapReady;
    }

    const tmp = ['geometry'];
    if (this._config.geography) {
      tmp.push('geography');
    }
    const libraries = tmp.join();

    // create a new script element with the API key and callback function name
    const script = this._document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js?libraries=' +
      libraries +
      '&callback=' +
      this._callback +
      '&key=' +
      environment.googleMapsApiKey;
    script.type = 'text/javascript';
    script.id = this._scriptId;
    script.async = true;
    script.defer = true;

    this._mapReady = this.createScriptObservable(script);

    // append to the document triggering download
    this._document.body.appendChild(script);

    return this._mapReady;
  }

  //
  // Wrap the script error handler and map API loader callback in an observable.
  // Accepts an existing callback th
  //
  private createScriptObservable(
    script: HTMLElement,
    callback: Function = undefined,
  ): Observable<void> {
    const scriptObservable = new Subject<void>();

    const onerror: Function = script.onerror; // existing handler
    script.onerror = (error: Event) => {
      // assign an error callback
      scriptObservable.error(error);

      if (onerror) {
        onerror(); // invoke the original error callback
      }
    };

    (<any>this.window)[this._callback] = () => {
      // success callback
      scriptObservable.next();
      scriptObservable.complete();

      if (callback) {
        // existing callback?
        callback();
      }
    };

    return scriptObservable;
  }
}
